import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import HolographicBg from "@/components/HolographicBg";
import TopBar from "@/components/TopBar";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

export default function Vault() {
  const { user, loading } = useAuth();
  const [items, setItems] = useState([]);
  const [buying, setBuying] = useState(null); // merch item selected for purchase
  const navigate = useNavigate();

  useEffect(() => {
    if (loading) return;
    if (!user || (user.role !== "influencer" && user.role !== "admin")) { navigate("/influencer/login"); return; }
    api.get("/merch").then(({ data }) => setItems(data)).catch(() => {});
  }, [user, loading, navigate]);

  return (
    <div className="relative min-h-screen">
      <HolographicBg />
      <TopBar />
      <main className="relative z-20 px-6 py-10 max-w-6xl mx-auto" data-testid="identity-vault">
        <div className="font-mono text-[10px] tracking-[0.4em] uppercase text-fuchsia-400">— identity vault —</div>
        <h1 className="font-display text-4xl sm:text-5xl font-black uppercase mt-2 neon-magenta">Influencer Drops</h1>
        <p className="font-mono text-xs uppercase tracking-widest text-white/60 mt-3 max-w-xl">
          Engraved keychains. Metal club tags. Bag clips. Pendants. Limited collectibles for the marked few.
        </p>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mt-10">
          {items.length === 0 && (
            <div className="font-mono text-sm text-white/50 col-span-full">The vault is empty. Drops appear soon.</div>
          )}
          {items.map((m) => {
            const soldOut = (m.stock ?? 0) <= 0;
            const noPrice = !m.price || m.price <= 0;
            const canBuy = !soldOut && !noPrice;
            return (
              <div
                key={m.id}
                className="glass-strong rounded-sm overflow-hidden border border-white/10 hover:border-fuchsia-400/60 hover:shadow-[0_0_30px_rgba(255,0,255,0.15)] transition"
                data-testid={`vault-item-${m.id}`}
              >
                {m.image_url ? (
                  <div className="aspect-square w-full bg-black overflow-hidden">
                    <img src={m.image_url} alt={m.name} className="w-full h-full object-cover" />
                  </div>
                ) : (
                  <div className="aspect-square w-full bg-gradient-to-br from-fuchsia-900/40 via-black to-cyan-900/40 flex items-center justify-center font-display text-5xl font-black text-white/20">◬</div>
                )}
                <div className="p-5">
                  <div className="flex items-start justify-between gap-2">
                    <div className="font-display font-bold uppercase text-lg leading-tight">{m.name}</div>
                    {!noPrice && (
                      <div className="font-mono text-sm text-cyan-400 whitespace-nowrap" data-testid={`vault-price-${m.id}`}>
                        ${Number(m.price).toFixed(2)}
                      </div>
                    )}
                  </div>
                  <div className="font-mono text-[10px] uppercase tracking-widest text-fuchsia-400 mt-1">
                    {m.tier} · {soldOut ? "sold out" : `${m.stock} left`}
                  </div>
                  <p className="font-mono text-xs text-white/60 mt-3 line-clamp-3">{m.description}</p>
                  <button
                    onClick={() => canBuy && setBuying(m)}
                    disabled={!canBuy}
                    data-testid={`vault-buy-btn-${m.id}`}
                    className="mt-4 w-full gate py-3 bg-fuchsia-500 text-black font-display font-bold uppercase tracking-[0.3em] text-xs rounded-sm hover:scale-[1.02] transition disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:scale-100"
                  >
                    {soldOut ? "Sold Out" : noPrice ? "Coming Soon" : "Claim Relic"}
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      </main>

      {buying && <PurchaseModal item={buying} user={user} onClose={() => setBuying(null)} />}
    </div>
  );
}

function PurchaseModal({ item, user, onClose }) {
  const [f, setF] = useState({
    ship_name: user?.full_name || "",
    ship_address1: "",
    ship_address2: "",
    ship_city: "",
    ship_region: "",
    ship_postal: "",
    ship_country: "United States",
    ship_phone: "",
  });
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const { data } = await api.post(`/merch/${item.id}/checkout`, {
        ...f,
        origin_url: window.location.origin,
      });
      if (data.url) {
        window.location.href = data.url;
      } else {
        toast.error("Checkout URL missing");
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not start checkout");
      setSubmitting(false);
    }
  };

  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/85 backdrop-blur-sm px-4 py-8 overflow-y-auto" onClick={onClose} data-testid="vault-purchase-modal">
      <div className="glass-strong rounded-sm p-8 max-w-lg w-full border border-fuchsia-400/30 my-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between gap-4">
          <div>
            <div className="font-mono text-[10px] tracking-[0.4em] uppercase text-fuchsia-400/80">— claim relic —</div>
            <h3 className="font-display text-2xl font-black uppercase mt-2 leading-tight">{item.name}</h3>
            <div className="font-mono text-sm text-cyan-400 mt-1">${Number(item.price).toFixed(2)} · {item.stock} left</div>
          </div>
          <button onClick={onClose} className="text-white/50 hover:text-white text-2xl leading-none" data-testid="vault-purchase-close">×</button>
        </div>

        <form onSubmit={submit} className="mt-6 space-y-3" data-testid="vault-purchase-form">
          <input required placeholder="Full name (as on ID)" value={f.ship_name} onChange={set("ship_name")} data-testid="vault-ship-name" className="w-full px-4 py-3 bg-black/60 border border-white/15 rounded-sm font-mono text-sm focus:outline-none focus:border-fuchsia-400/60" />
          <input required placeholder="Street address" value={f.ship_address1} onChange={set("ship_address1")} data-testid="vault-ship-addr1" className="w-full px-4 py-3 bg-black/60 border border-white/15 rounded-sm font-mono text-sm focus:outline-none focus:border-fuchsia-400/60" />
          <input placeholder="Apt / Suite (optional)" value={f.ship_address2} onChange={set("ship_address2")} data-testid="vault-ship-addr2" className="w-full px-4 py-3 bg-black/60 border border-white/15 rounded-sm font-mono text-sm focus:outline-none focus:border-fuchsia-400/60" />
          <div className="grid grid-cols-2 gap-3">
            <input required placeholder="City" value={f.ship_city} onChange={set("ship_city")} data-testid="vault-ship-city" className="w-full px-4 py-3 bg-black/60 border border-white/15 rounded-sm font-mono text-sm focus:outline-none focus:border-fuchsia-400/60" />
            <input required placeholder="State / Region" value={f.ship_region} onChange={set("ship_region")} data-testid="vault-ship-region" className="w-full px-4 py-3 bg-black/60 border border-white/15 rounded-sm font-mono text-sm focus:outline-none focus:border-fuchsia-400/60" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <input required placeholder="ZIP / Postal" value={f.ship_postal} onChange={set("ship_postal")} data-testid="vault-ship-postal" className="w-full px-4 py-3 bg-black/60 border border-white/15 rounded-sm font-mono text-sm focus:outline-none focus:border-fuchsia-400/60" />
            <input required placeholder="Country" value={f.ship_country} onChange={set("ship_country")} data-testid="vault-ship-country" className="w-full px-4 py-3 bg-black/60 border border-white/15 rounded-sm font-mono text-sm focus:outline-none focus:border-fuchsia-400/60" />
          </div>
          <input placeholder="Phone (in case courier needs it)" value={f.ship_phone} onChange={set("ship_phone")} data-testid="vault-ship-phone" className="w-full px-4 py-3 bg-black/60 border border-white/15 rounded-sm font-mono text-sm focus:outline-none focus:border-fuchsia-400/60" />
          <div className="pt-2 flex gap-3">
            <button
              type="submit"
              disabled={submitting}
              data-testid="vault-purchase-submit"
              className="gate flex-1 py-3 bg-fuchsia-500 text-black font-display font-bold uppercase tracking-[0.3em] text-xs rounded-sm hover:scale-[1.02] transition disabled:opacity-50"
            >
              {submitting ? "Redirecting..." : `Pay $${Number(item.price).toFixed(2)}`}
            </button>
            <button type="button" onClick={onClose} disabled={submitting} className="px-5 py-3 border border-white/20 text-white/70 font-display font-bold uppercase tracking-[0.3em] text-xs rounded-sm hover:bg-white/5 disabled:opacity-50">
              Cancel
            </button>
          </div>
          <p className="font-mono text-[10px] text-white/40 mt-2 leading-relaxed">
            Each piece is hand-made by a divine designer once your order lands.
            Lead time can be <span className="text-fuchsia-400/80">up to one month</span> — you will be emailed the moment your relic ships.
            Address cannot be changed once packed.
          </p>
        </form>
      </div>
    </div>
  );
}
